// src/pages/Login.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase';
import { signInWithEmailAndPassword, sendPasswordResetEmail, browserLocalPersistence } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../context/AuthContext';
import { getTeacherByAuthUid } from '../utils/teacherHelper';
import { getChildren } from '../utils/childrenHelper';
import { TRIAL_SIGNUP_ENABLED } from '../config/featureFlags';
import {
  readPendingTrialSignup,
  recordPendingTrialSignupAttempt,
  clearPendingTrialSignup,
  MAX_RESUME_ATTEMPTS,
} from '../utils/pendingTrialSignup';
import { readEnrolReturn, clearEnrolReturn } from '../utils/pendingEnrol';
import { Crown, Mail, Lock, ArrowRight, Sparkles, Bot, Headset, ChartArea, Gamepad2, LogIn, GraduationCap, Eye, EyeOff } from 'lucide-react';
import Footer from '../components/Footer';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { user, authLoading } = useAuth();

  // Shown when a parent was sent here from /trial because their email already
  // has an account.
  const fromTrialSignup = !!location.state?.fromTrialSignup;

  const showLoginForm = () => {
    setShowForgotPassword(false);
    setResetSent(false);
    setError(null);
  };

useEffect(() => {
  const checkSubscriptionAndRedirect = async () => {
    if (authLoading) return;
    
    if (user) {
      try {
        // ── Resume a pending trial signup ────────────────────────────────────
        // Set by TrialSignup when the email was already registered: the parent
        // logged in here so their trial request attaches to THIS account.
        // One attempt here; the stash is cleared ONLY on success. On failure we
        // hand off to /trial-status, which shows a "try again" card (the request
        // is never silently lost). Guarded to the email the stash was built for.
        if (TRIAL_SIGNUP_ENABLED) {
          const pending = readPendingTrialSignup(user.email);
          if (pending) {
            if (pending.attempts < MAX_RESUME_ATTEMPTS) {
              recordPendingTrialSignupAttempt();
              try {
                await httpsCallable(getFunctions(), 'createTrialSignup')(pending.payload);
                clearPendingTrialSignup();
              } catch (resumeErr) {
                console.error('Login: could not resume pending trial signup', resumeErr);
              }
            }
            navigate('/trial-status', { replace: true });
            return;
          }
        }

        // ── Return to /enroll ───────────────────────────────────────────────
        // Set by TrialEnroll when it bounced a signed-out visitor here (e.g. a
        // trial_followup email link opened while logged out). Honoured AFTER the
        // pending-trial-signup resume above (a half-finished trial request must
        // still complete first) and BEFORE the course-status ladder, so the
        // parent lands on the page they came for. Only /enroll ever sets this,
        // so no other user's landing changes. Flag-gated — /enroll only exists
        // when TRIAL_SIGNUP_ENABLED.
        if (TRIAL_SIGNUP_ENABLED && readEnrolReturn()) {
          clearEnrolReturn();
          navigate('/enroll', { replace: true });
          return;
        }

        // Check user's course status
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        
        if (!userDoc.exists()) {
          // No parent/user doc — this may be a teacher account. Route
          // teachers to the portal before the signup funnel.
          try {
            const teacher = await getTeacherByAuthUid(user.uid);
            if (teacher) {
              navigate("/teacher", { replace: true });
              return;
            }
          } catch (teacherLookupErr) {
            console.error('Login teacher lookup error:', teacherLookupErr);
            // fall through to select-plan on error
          }
          // User exists in auth but not in Firestore - redirect to signup
          navigate("/select-plan", { replace: true });
          return;
        }

        const userData = userDoc.data();
        
        // Check if ANY child has an active course
        let hasActiveCourse = false;
        let hasEndedCourse = false; // completed or expired = returning customer
        if (userData.children && Array.isArray(userData.children)) {
          hasActiveCourse = userData.children.some(child => child.course?.status === 'active');
          hasEndedCourse = userData.children.some(child =>
            child.course?.status === 'completed' || child.course?.status === 'expired'
          );
        } else if (userData.course?.status) {
          // Pre-migration safety net
          hasActiveCourse = userData.course.status === 'active';
          hasEndedCourse = userData.course.status === 'completed' || userData.course.status === 'expired';
        }

        // Any course object at all (any status) — mirrors the ProtectedRoute
        // trial guard's hasAnyCourse.
        const hasAnyCourse = getChildren(userData).some(child => !!child.course);

        if (hasActiveCourse) {
          navigate("/", { replace: true });
        } else if (hasEndedCourse) {
          // Returning customer whose course ended -> renewal flow, NOT signup completion
          navigate("/course-renewal", { replace: true });
        } else if (TRIAL_SIGNUP_ENABLED && userData.signupSource === 'trial' && !hasAnyCourse) {
          // Trial signup, no course yet — same condition as the ProtectedRoute
          // guard. Placed AFTER the active / ended checks (a converted trial or
          // a returning paying customer must never land here) and BEFORE the
          // final else (an all-pending unpaid customer still goes to payment).
          navigate("/trial-status", { replace: true });
        } else {
          // Genuinely new / unpaid customer
          navigate("/complete-subscription", { replace: true });
        }
      } catch (error) {
        console.error('Error checking course status:', error);
        // On error, redirect to complete subscription for safety
        navigate("/complete-subscription", { replace: true });
      }
    }
  };

  checkSubscriptionAndRedirect();
}, [user, authLoading, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setError(null);
    
    try {
      await auth.setPersistence(browserLocalPersistence);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Track login timestamp for admin analytics
      // Fire-and-forget: we don't await or block the login flow on this
      try {
        await updateDoc(doc(db, 'users', userCredential.user.uid), {
          'analytics.lastLogin': new Date().toISOString()
        });
      } catch (trackingErr) {
        // Non-critical — silently ignore if tracking fails
        console.warn('Could not record login timestamp:', trackingErr);
      }
    } catch (err) {
      setError(getErrorMessage(err.code));
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!resetEmail) {
      setError('Please enter your email address');
      return;
    }

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetSent(true);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err.code));
      setResetSent(false);
    }
  };

  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/user-not-found':
        return 'No account found with this email. Please check your email or create a new account.';
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again.';
      case 'auth/invalid-credential':
        return 'Incorrect email or password. Please try again, or use "Forgot your password?" to reset it.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/user-disabled':
        return 'This account has been disabled. Please contact support.';
      case 'auth/too-many-requests':
        return 'Too many failed attempts. Please try again later.';
      default:
        return 'An error occurred. Please try again.';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="grid lg:grid-cols-2 gap-0">
              {/* Left Side - Title and Features */}
              <div className="bg-gradient-to-br from-purple-600 to-indigo-700 p-6 sm:p-8 text-white flex flex-col justify-center">
                <div className="max-w-md mx-auto lg:mx-0">
                  {/* Brand Badge */}
                  <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-base sm:text-lg font-bold mb-6">
                    <Sparkles className="text-yellow-400 flex-shrink-0" size={20} />
                    <span className="whitespace-nowrap">My Roots</span>
                    <Sparkles className="text-yellow-400 flex-shrink-0" size={20} />
                  </div>

                  {/* Title */}
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 leading-tight">
                    Where Kids Discover Arabic Through Play
                  </h1>
                  <p className="text-purple-100 text-base sm:text-lg mb-8 leading-relaxed">
                    A playful and engaging way for your child to learn Arabic with joy and confidence.
                  </p>

                  {/* Features - MOBILE FIXED */}
                  <div className="space-y-4 max-w-md">
                    <div className="flex items-start gap-3 text-white/90">
                      <div className="w-10 h-10 sm:w-8 sm:h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 sm:mt-0">
                        <GraduationCap size={18} className="text-white" />
                      </div>
                      <span className="font-semibold text-sm sm:text-base leading-relaxed">Online Arabic classes with professional teachers</span>
                    </div>
                    <div className="flex items-start gap-3 text-white/90">
                      <div className="w-10 h-10 sm:w-8 sm:h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 sm:mt-0">
                        <Gamepad2 size={18} className="text-white" />
                      </div>
                      <span className="font-semibold text-sm sm:text-base leading-relaxed">12+ interactive games teaching language, numbers, and culture</span>
                    </div>
                    <div className="flex items-start gap-3 text-white/90">
                      <div className="w-10 h-10 sm:w-8 sm:h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 sm:mt-0">
                        <Bot size={18} className="text-white" />
                      </div>
                      <span className="font-semibold text-sm sm:text-base leading-relaxed">AI tutor to answer questions anytime</span>
                    </div>
                    <div className="flex items-start gap-3 text-white/90">
                      <div className="w-10 h-10 sm:w-8 sm:h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 sm:mt-0">
                        <ChartArea size={18} className="text-white" />
                      </div>
                      <span className="font-semibold text-sm sm:text-base leading-relaxed">Progress tracking to see growth over time</span>
                    </div>
                    <div className="flex items-start gap-3 text-white/90">
                      <div className="w-10 h-10 sm:w-8 sm:h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 sm:mt-0">
                        <Headset size={18} className="text-white" />
                      </div>
                      <span className="font-semibold text-sm sm:text-base leading-relaxed">Real people, always here to help</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Side - Login Form */}
              <div className="bg-white p-6 sm:p-8 flex justify-center lg:justify-end">
                <div className="w-full max-w-lg">
                  {!showForgotPassword ? (
                    <>
                      <div className="text-center mb-8">
                        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-full text-sm font-bold mb-4">
                          <Crown size={16} className="flex-shrink-0" />
                          <span>Premium Access</span>
                        </div>
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Sign In to Your Account</h2>
                      </div>

                      {fromTrialSignup && (
                        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                          <div className="text-blue-800 text-sm font-medium">
                            You already have an account. Sign in and we'll finish your trial request.
                          </div>
                        </div>
                      )}

                      <form onSubmit={handleLogin} className="space-y-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Email Address
                          </label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 flex-shrink-0" size={20} />
                            <input 
                              type="email"
                              value={email} 
                              onChange={(e) => setEmail(e.target.value)} 
                              required
                              className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all duration-200 bg-white text-base"
                              placeholder="Enter your email"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Password
                          </label>
                          <div className="relative">
                            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 flex-shrink-0" size={20} />
                            <input 
                              type={showPassword ? "text" : "password"}
                              value={password} 
                              onChange={(e) => setPassword(e.target.value)} 
                              required
                              className="w-full pl-12 pr-12 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all duration-200 bg-white text-base"
                              placeholder="Enter your password"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="password-toggle absolute w-8 h-8 flex items-center justify-center text-gray-400 hover:text-purple-500 transition-colors duration-200 bg-transparent border-0 outline-none appearance-none p-0 cursor-pointer"
                              style={{ top: '50%', right: '12px', transform: 'translateY(-50%)' }}
                              aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                          </div>
                        </div>

                        <button 
                          type="submit" 
                          disabled={isLoggingIn}
                          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:transform-none shadow-lg hover:shadow-xl"
                        >
                          {isLoggingIn ? (
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              <span>Signing In...</span>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center gap-2">
                              <LogIn size={20} className="flex-shrink-0" />
                              <span>Sign In</span>
                              <ArrowRight size={20} className="flex-shrink-0" />
                            </div>
                          )}
                        </button>

                        <button 
                          type="button"
                          onClick={() => {
                            setShowForgotPassword(true);
                            setResetEmail(email);
                            setError(null);
                          }}
                          className="w-full text-purple-600 hover:text-purple-700 font-semibold hover:underline"
                        >
                          Forgot your password?
                        </button>
                      </form>

                      <div className="mt-6 pt-6 border-t border-gray-200 text-center">
                        <p className="text-gray-600 mb-3">Don't have an account?</p>
                        <button 
                          onClick={() => navigate("/select-plan")}
                          className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-xl transition-all duration-200 w-full"
                        >
                          Create Premium Account
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="text-center">
                      <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-6">Reset Your Password</h3>
                      {resetSent ? (
                        <div className="space-y-4">
                          <div className="text-green-600 text-6xl mb-4">✅</div>
                          <p className="text-gray-700 mb-6 text-sm sm:text-base">
                            Password reset email sent to <strong>{resetEmail}</strong>
                          </p>
                          <button 
                            onClick={showLoginForm}
                            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-3 px-6 rounded-xl transition-all duration-200"
                          >
                            Back to Sign In
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-6">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Email Address
                            </label>
                            <div className="relative">
                              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 flex-shrink-0" size={20} />
                              <input 
                                type="email"
                                value={resetEmail} 
                                onChange={(e) => setResetEmail(e.target.value)} 
                                required
                                className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all duration-200 bg-white text-base"
                                placeholder="Enter your email"
                              />
                            </div>
                          </div>
                          
                          <button 
                            onClick={handlePasswordReset}
                            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
                          >
                            Send Reset Email
                          </button>
                          
                          <button 
                            onClick={showLoginForm}
                            className="w-full text-purple-600 hover:text-purple-700 font-semibold hover:underline"
                          >
                            Back to Sign In
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Error Message */}
                  {error && (
                    <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                      <div className="text-red-700 text-sm font-medium">{error}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer variant="minimal" />
      
      {/* Mobile-specific CSS fixes */}
      <style jsx>{`
        /* Critical mobile fixes */
        @media (max-width: 768px) {
          /* Prevent input zoom on iOS */
          input[type="email"], input[type="password"] {
            font-size: 16px !important;
          }
          
          /* Fix icon container aspect ratio */
          .icon-container {
            aspect-ratio: 1 / 1;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            flex-shrink: 0 !important;
          }
          
          /* Improve touch targets */
          button {
            min-height: 48px;
            min-width: 48px;
          }

          /* Exception for password toggle - fixed size to prevent layout shift */
          button.password-toggle {
            min-height: unset !important;
            min-width: unset !important;
            width: 32px !important;
            height: 32px !important;
          }
          
          /* Better text wrapping */
          .feature-text {
            word-wrap: break-word;
            hyphens: auto;
          }
        }
        
        /* iOS Safari specific fixes */
        @supports (-webkit-touch-callout: none) {
          /* Safe area handling */
          .container {
            padding-left: max(1rem, env(safe-area-inset-left));
            padding-right: max(1rem, env(safe-area-inset-right));
          }
          
          /* Prevent viewport zoom */
          input {
            font-size: 16px;
            -webkit-text-size-adjust: 100%;
          }
        }
        
        /* Android specific fixes */
        @media (max-width: 768px) and (-webkit-min-device-pixel-ratio: 1) {
          /* Better rendering on Android */
          .rounded-full {
            border-radius: 50% !important;
          }
        }
      `}</style>
    </div>
  );
}

export default Login;